import React from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  JurisprudenceAlertItem,
  AlertStatus,
  AlertBucket,
} from '../hooks/useJurisprudenceAlerts';

interface Props {
  alerts: JurisprudenceAlertItem[];
  isLoading: boolean;
  total: number;
  page: number;
  totalPages: number;
  onPageChange: (next: number) => void;
  onRowClick?: (alert: JurisprudenceAlertItem) => void;
}

const STATUS_LABELS: Record<AlertStatus, string> = {
  sent: 'Envoyée',
  pending: 'En attente',
  failed: 'Échec',
  suppressed: 'Supprimée',
};

const STATUS_BADGE: Record<AlertStatus, string> = {
  sent: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200',
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200',
  suppressed: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
};

const BUCKET_LABELS: Record<AlertBucket, string> = {
  immediate: 'Immédiat',
  daily: 'Quotidien',
  weekly: 'Hebdomadaire',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatScore(score: number): string {
  return `${Math.round(score * 100)}%`;
}

const AlertsTable: React.FC<Props> = ({
  alerts,
  isLoading,
  total,
  page,
  totalPages,
  onPageChange,
  onRowClick,
}) => {
  const navigate = useNavigate();

  if (!isLoading && alerts.length === 0) {
    return (
      <div className="bg-white dark:bg-mcdm-third-400 rounded-lg border border-gray-200 dark:border-gray-700 py-16 text-center text-mcdm-third-500 dark:text-mcdm-primary-200">
        <p className="text-base">Aucune alerte pour les critères sélectionnés.</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-mcdm-third-400 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <p className="text-sm text-mcdm-third-500 dark:text-mcdm-primary-200">
          {total} alerte{total > 1 ? 's' : ''}
        </p>
        {isLoading && <span className="text-xs text-gray-400">Chargement…</span>}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-mcdm-third-500">
            <tr className="text-left text-xs uppercase tracking-wider text-mcdm-third-500 dark:text-mcdm-primary-100">
              <th className="px-4 py-3 font-medium">Jurisprudence</th>
              <th className="px-4 py-3 font-medium">Juridiction</th>
              <th className="px-4 py-3 font-medium">Statut</th>
              <th className="px-4 py-3 font-medium">Fréquence</th>
              <th className="px-4 py-3 font-medium">Score</th>
              <th className="px-4 py-3 font-medium">Envoyée le</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {alerts.map(a => (
              <tr
                key={a._id}
                onClick={() =>
                  onRowClick ? onRowClick(a) : navigate(`/jurisprudences/${a.jurisprudenceId}`)
                }
                className="cursor-pointer hover:bg-gray-50 dark:hover:bg-mcdm-third-300 transition-colors"
              >
                <td className="px-4 py-3 text-mcdm-third-500 dark:text-mcdm-primary-100">
                  <div className="font-medium line-clamp-2">
                    {a.juris?.title ?? `Affaire ${a.jurisprudenceId}`}
                  </div>
                </td>
                <td className="px-4 py-3 text-mcdm-third-500 dark:text-mcdm-primary-200">
                  {a.juris?.jurisdiction || '—'}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-block px-2 py-1 rounded text-xs font-medium ${STATUS_BADGE[a.status]}`}
                  >
                    {STATUS_LABELS[a.status]}
                  </span>
                </td>
                <td className="px-4 py-3 text-mcdm-third-500 dark:text-mcdm-primary-200">
                  {BUCKET_LABELS[a.bucket]}
                </td>
                <td className="px-4 py-3 text-mcdm-third-500 dark:text-mcdm-primary-200">
                  {formatScore(a.matchScore)}
                </td>
                <td className="px-4 py-3 text-mcdm-third-500 dark:text-mcdm-primary-200">
                  {formatDate(a.dispatchedAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <p className="text-xs text-mcdm-third-500 dark:text-mcdm-primary-200">
            Page {page} / {totalPages}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
              className="px-3 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded text-mcdm-third-500 dark:text-mcdm-primary-100 hover:bg-gray-50 dark:hover:bg-mcdm-third-500 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Précédent
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
              className="px-3 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded text-mcdm-third-500 dark:text-mcdm-primary-100 hover:bg-gray-50 dark:hover:bg-mcdm-third-500 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Suivant
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertsTable;
